import { useEffect, useState } from "react";
import {
  Input,
  Select,
  TextArea,
  DatePicker,
  InputDropdown,
} from "../../../shared";
import { formatDate } from "../../../utils";
import { AvatarClient, ClientsDropdown } from "../../../components";
import { useConfig } from "../../../hooks";
import {
  EQUIPMENT_CATEGORY_OPTIONS,
  EQUIPMENT_STATUS_OPTIONS,
  FUEL_TYPE_OPTIONS,
  getEquipmentTypeOptions,
} from "../../../constants/select";

const EquipmentCreateForm = ({
  form = {},
  updateForm,
  clients = [],
  clientId,
  searchTerm = "",
  setSearchTerm,
  handleSelectClient,
}) => {
  const { appType } = useConfig();
  const [typeOptions, setTypeOptions] = useState([]);

  // Update type options when equipment category changes
  useEffect(() => {
    if (form.equipmentCategory) {
      const options = getEquipmentTypeOptions(form.equipmentCategory);
      setTypeOptions(options);
    }
  }, [form.equipmentCategory]);

  // Add useEffect to handle clientId prop
  useEffect(() => {
    if (clientId) {
      const selectedClient = clients.find((c) => c._id === clientId);
      if (selectedClient) {
        updateForm({ id: "client", value: clientId });
      }
    }
  }, [clientId, clients, updateForm]);

  // Helper function to get value from form object
  const getValue = (valueId) => {
    if (!form[valueId]) return "";
    return form[valueId];
  };

  // Set form values as variables for easier access
  const name = getValue("name");
  const type = getValue("type");
  const serialNumber = getValue("serialNumber");
  const model = getValue("model");
  const manufacturer = getValue("manufacturer");
  const purchaseDate = getValue("purchaseDate");
  const status = getValue("status");
  const notes = getValue("notes");
  const client = getValue("client");
  const odometer = getValue("odometer");
  const equipmentCategory = getValue("equipmentCategory");
  const vin = getValue("vin");
  const licensePlate = getValue("licensePlate");
  const year = getValue("year");
  const dotNumber = getValue("dotNumber");
  const fuelType = getValue("fuelType");
  const registrationExpiry = getValue("registrationExpiry");
  const insuranceExpiry = getValue("insuranceExpiry");
  const lastInspectionDate = getValue("lastInspectionDate");
  const nextInspectionDue = getValue("nextInspectionDue");

  // Function to handle form updates
  const handleChange = (e) => {
    updateForm({ id: e.id || e.target.id, value: e.value });
  };

  // Function to handle select values
  const getSelectValue = (field, options = EQUIPMENT_STATUS_OPTIONS) => {
    if (!field) return null;
    const option = options.find((opt) => opt.value === field);
    return option || null;
  };

  // Filter equipment categories by appType
  const filteredCategories = EQUIPMENT_CATEGORY_OPTIONS.filter((cat) =>
    cat.appType.includes(appType)
  );

  // Add helper to get client name
  const getClientName = (clientId, clients) => {
    if (!clientId || !clients.length) return "";
    const selectedClient = clients.find((c) => c._id === clientId);
    return selectedClient ? selectedClient.name : "";
  };

  // Add helper to format equipment type
  const formatEquipmentType = (type) => {
    return type?.startsWith("-") ? type.substring(1).trim() : type;
  };

  // Add helper to get status value
  const getStatusValue = (status) => {
    return status?.value || status || "";
  };

  // Function to render client information or search dropdown
  const renderClientSection = () => {
    // If clientId is passed, don't show any client selection UI
    if (clientId) {
      return null;
    }

    // If client is selected through normal flow
    // if (form.client) {
    //   const displayName = getClientName(form.client, clients);
    //   return (
    //     <Input
    //       label="Client"
    //       value={displayName}
    //       disabled
    //       rightElement={
    //         <button
    //           onClick={() => {
    //             updateForm({ id: "client", value: null });
    //             setSearchTerm("");
    //           }}
    //           className="text-red-500 hover:text-red-700"
    //         >
    //           ✕
    //         </button>
    //       }
    //     />
    //   );
    // }

    // Show dropdown for client selection if no client is selected
    return (
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-900">
          Create Equipment
        </label>
        <ClientsDropdown
          form={form}
          updateForm={updateForm}
          hideFooter
          onClick={(client) => updateForm({ id: "client", value: client })}
          value={form.client}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 w-full">
        <Input
          label="Equipment Name"
          value={name}
          onChange={(e) => handleChange({ id: "name", value: e.value })}
          required
          className="w-full"
        />
        <Select
          label="Equipment Category"
          value={equipmentCategory}
          onChange={(value) => {
            handleChange({ id: "equipmentCategory", value: value.value });
            // Clear type when category changes
            handleChange({ id: "type", value: "" });
          }}
          options={filteredCategories}
          required
          className="w-full"
        />
      </div>

      {/* Show type dropdown only if category is selected */}
      {equipmentCategory && typeOptions.length > 0 && (
        <Select
          label="Equipment Type"
          value={formatEquipmentType(type)}
          onChange={(value) => handleChange({ id: "type", value: value.value })}
          options={typeOptions}
          required
          className="w-full"
        />
      )}

      {/* Trucking-specific fields */}
      {appType === "trucking" && (equipmentCategory === "truck" || equipmentCategory === "trailer") && (
        <>
          <div className="grid grid-cols-2 gap-4 w-full">
            <Input
              label="VIN"
              value={vin}
              onChange={(e) => handleChange({ id: "vin", value: e.value })}
              placeholder="Enter VIN number"
              className="w-full"
            />
            <Input
              label="License Plate"
              value={licensePlate}
              onChange={(e) => handleChange({ id: "licensePlate", value: e.value })}
              placeholder="Enter license plate"
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 w-full">
            <Input
              label="Year"
              type="number"
              value={year}
              onChange={(e) => handleChange({ id: "year", value: e.value })}
              placeholder="e.g., 2024"
              className="w-full"
            />
            <Input
              label="DOT Number"
              value={dotNumber}
              onChange={(e) => handleChange({ id: "dotNumber", value: e.value })}
              placeholder="Enter DOT number"
              className="w-full"
            />
          </div>

          <Select
            label="Fuel Type"
            value={fuelType}
            onChange={(value) => handleChange({ id: "fuelType", value: value.value })}
            options={FUEL_TYPE_OPTIONS}
            className="w-full"
          />
        </>
      )}
      <Input
        label="Serial Number"
        value={serialNumber}
        onChange={(e) => handleChange({ id: "serialNumber", value: e.value })}
      />
      <div className="grid grid-cols-2 gap-4 w-full">
        <Input
          label="Model"
          value={model}
          onChange={(e) => handleChange({ id: "model", value: e.value })}
        />
        <Input
          label="Manufacturer"
          value={manufacturer}
          onChange={(e) => handleChange({ id: "manufacturer", value: e.value })}
        />
      </div>
      {/* ServiceRepair-specific fields */}
      {appType === "serviceRepair" && (
        <Input
          label="Hour Meter"
          type="number"
          value={odometer}
          onChange={(e) => handleChange({ id: "odometer", value: e.value })}
          placeholder="Enter current hour meter reading"
        />
      )}

      <div className="grid grid-cols-2 gap-4 w-full">
        <DatePicker
          label="Purchase Date"
          value={formatDate(purchaseDate)}
          onChange={(e) => handleChange({ id: "purchaseDate", value: e.value })}
        />
        <Select
          label="Status"
          value={getStatusValue(status)}
          onChange={(value) =>
            handleChange({ id: "status", value: value.value })
          }
          options={EQUIPMENT_STATUS_OPTIONS}
          required
        />
      </div>

      {/* Trucking compliance dates */}
      {appType === "trucking" && (equipmentCategory === "truck" || equipmentCategory === "trailer") && (
        <>
          <div className="grid grid-cols-2 gap-4 w-full">
            <DatePicker
              label="Registration Expiry"
              value={formatDate(registrationExpiry)}
              onChange={(e) => handleChange({ id: "registrationExpiry", value: e.value })}
            />
            <DatePicker
              label="Insurance Expiry"
              value={formatDate(insuranceExpiry)}
              onChange={(e) => handleChange({ id: "insuranceExpiry", value: e.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 w-full">
            <DatePicker
              label="Last Inspection Date"
              value={formatDate(lastInspectionDate)}
              onChange={(e) => handleChange({ id: "lastInspectionDate", value: e.value })}
            />
            <DatePicker
              label="Next Inspection Due"
              value={formatDate(nextInspectionDue)}
              onChange={(e) => handleChange({ id: "nextInspectionDue", value: e.value })}
            />
          </div>
        </>
      )}
      {/* Client selection for serviceRepair only */}
      {appType === "serviceRepair" && renderClientSection()}
      <TextArea
        label="Notes"
        value={notes}
        onChange={(e) => handleChange({ id: "notes", value: e.target.value })}
      />
    </div>
  );
};

export default EquipmentCreateForm;
